import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { cn } from "@/lib/utils";
import { Input } from "./ui/input";
import { clamp } from "@/utils/math";
import { Textarea } from "./TextArea";
import { Button } from "./ui/button";
import { Dialog } from "./Dialog";
import * as BaseDialog from "@/components/ui/dialog";
import { TaskMarkdown } from "./TaskMarkdown";

const guide =
    `
**Så använder du tvättlinan:**<br><br>
- Skriv in din nuvarande ålder längst ner till vänster.<br><br>
- Dra sedan klädnypan ovanför linjen och placera den där du vill markera en händelse, åldern räknas ut automatiskt.<br><br>
- Skriv in vad som hände vid den tidpunkten. Upprepa med fler klädnypor för att kartlägga din livslinje.<br><br>
- Tvättlinan fungerar endast i app läge just nu, inte i web browser. 

`;

type Point = { x: number, y: number };
export type PinData = {
    text: string;
    position: Point;
    index: number;
};

export type TimelineConfig = {
    start: number;
    end: number;
};

export type TimelineData = {
    config: TimelineConfig;
    pins: PinData[];
    completed?: boolean;
};

type TimelineTaskProps = {
    initialState: TimelineData;
    onStateChanged: (newState: TimelineData) => void;
    handleResizeRef?: React.MutableRefObject<() => void>;
    onDone?: () => void;
};

export function TimelineTask({ handleResizeRef, initialState, onStateChanged, onDone }: TimelineTaskProps) {
    const [data, setData] = useState<TimelineData>(initialState ?? {
        config: {
            start: 1,
            end: 20
        },
        completed: false,
        pins: [],
    });

    const pinContainerRef = useRef<HTMLDivElement>(null);
    const mainContainerRef = useRef<HTMLDivElement>(null);

    const [newPinPos, setNewPinPos] = useState<Point>({ x: 0, y: 0 });
    const [placingNewPin, setPlacingNewPin] = useState(false);

    const timelineRange = initialState?.config?.end - initialState?.config?.start;

    const [mainContainerBounds, setMainContainerBounds] = useState<DOMRect>(null);
    const [pinContainerBounds, setPinContainerBounds] = useState<DOMRect>(null);

    const [showRemoveEffect, setShowRemoveEffect] = useState(false);

    const minPinYRef = useRef(0);

    useEffect(() => {
        const handleResize = () => {
            if (!pinContainerRef?.current || !mainContainerRef?.current) return;
            const pinContainerBounds = pinContainerRef.current.getBoundingClientRect();
            const mainContainerBounds = mainContainerRef.current.getBoundingClientRect();
            setPinContainerBounds(pinContainerBounds);
            setMainContainerBounds(mainContainerBounds);

            minPinYRef.current = (mainContainerBounds?.y - pinContainerBounds?.y) / pinContainerBounds?.height;
        };

        if (handleResizeRef) handleResizeRef.current = handleResize;

        const observer = new ResizeObserver(handleResize);
        if (pinContainerRef.current) observer.observe(pinContainerRef.current);
        if (mainContainerRef.current) observer.observe(mainContainerRef.current);

        return () => {
            observer.disconnect();
        };
    }, [handleResizeRef]);

    const handleNewPinMove = (event: React.TouchEvent<HTMLDivElement>) => {
        const localX = event.changedTouches[0].clientX - pinContainerBounds.x;
        const localY = (event.changedTouches[0].clientY - pinContainerBounds.y) / pinContainerBounds.height;
        const clampedY = clamp(localY, minPinYRef.current, 1);
        setNewPinPos({ x: localX, y: clampedY });
        setShowRemoveEffect(localY < 0);
    };

    const handleNewPinEnd = (event: React.TouchEvent<HTMLDivElement>) => {
        const localY = (event.changedTouches[0].clientY - pinContainerBounds.y) / pinContainerBounds?.height;
        const localX = 0; //event.changedTouches[0].clientX - pinContainerBounds.x;
        if (localY < 0) {
            setPlacingNewPin(false);
            setShowRemoveEffect(false);
            return;
        }

        const newPins = [
            ...data?.pins ?? [],
            {
                text: "",
                position: { x: localX, y: localY },
                index: data.pins.length,
            }
        ];
        const newState = {
            ...data,
            pins: newPins
        };
        setData(newState);
        onStateChanged(newState);
        setPlacingNewPin(false);
    };

    const setConfig = (newConfig: TimelineData["config"]) => {
        console.log(newConfig);
        const newState = {
            ...data,
            config: newConfig
        };
        setData(newState);
        onStateChanged(newState);
    };

    const setStart = (newStart: number) => {
        setConfig({
            ...data?.config,
            start: newStart,
        });
    };
    const setEnd = (newEnd: number) => {
        setConfig({
            ...data?.config,
            end: newEnd,
        });
    };
    const setPins = (newPins: TimelineData["pins"]) => {
        const newState = {
            ...data,
            pins: newPins
        };
        setData(newState);
        onStateChanged(newState);
    };
    const setPin = (pinIndex: number, { text, position }: { text?: string, position?: Point }) => {
        const newPins = [...data?.pins ?? []];
        if (text) newPins[pinIndex].text = text;
        if (position) newPins[pinIndex].position = position;
        setPins(newPins);
    };
    const removePin = (pinIndex: number) => {
        const newPins = [...data?.pins ?? []];
        newPins.splice(pinIndex, 1);
        setPins(newPins);
    };

    const handleNewPinStart = (event: React.TouchEvent<HTMLDivElement>) => {
        if (!placingNewPin) {
            setPlacingNewPin(true);
            handleNewPinMove(event);
        }
    };

    const calcYear = useCallback((yPosition: number) => {
        return data?.config?.start + timelineRange * yPosition;
    }, [data?.config?.start, timelineRange]);

    return (
        <div
            className="relative grow flex flex-col overflow-visible"
            onTouchEnd={placingNewPin ? handleNewPinEnd : undefined}
            onTouchMove={placingNewPin ? handleNewPinMove : undefined}
            ref={mainContainerRef}
        >
            <div
                className="relative z-10 mb-12"
            >
                <Pin
                    className="ml-[0.25rem]"
                    onTouchStart={handleNewPinStart}
                />
                {showRemoveEffect && <div className="absolute top-0 -left-6 -right-6 h-full bg-red-500/50 flex items-center justify-center">
                    <div className="text-lg">Släpp för att ta bort</div>
                </div>}
            </div>
            <div className="relative mb-12 grow grid grid-rows-1 grid-cols-[0.25rem_1fr]">

                <div className="relative col-start-1 bg-step-1">
                    {/* <Input
                        type="number"
                        min={0}
                        value={data?.config?.start}
                        onChange={(event) => setStart(+event.currentTarget.value)}
                        className="no-spin absolute -top-12 left-1/2 -translate-x-1/2 p-0 w-8 text-center"
                    /> */}
                    <p className="no-spin absolute -top-12 left-1/2 -translate-x-1/2 p-0 w-8 text-center">
                        {data?.config?.start}
                    </p>
                    <Input
                        type="number"
                        value={data?.config?.end}
                        max={150}
                        onChange={(event) => setEnd(event.currentTarget.value.length > 0 ? +event.currentTarget.value : NaN)}
                        className="no-spin absolute -bottom-12 left-1/2 -translate-x-1/2 p-0 w-8 text-center"
                    />
                </div>


                <div
                    ref={pinContainerRef}
                    className="col-start-2 relative overflow-visible"
                >
                    {pinContainerBounds && data?.pins?.map((pin, index) => (
                        <PinRight
                            key={pin.index}
                            minY={minPinYRef.current}
                            position={{ x: pin.position.x, y: pin.position.y }}
                            draggable={true}
                            text={pin.text}
                            calcYear={calcYear}
                            setPosition={(position) => {
                                setPin(index, {
                                    position: {
                                        y: clamp(position.y, 0, 1),
                                        x: data?.pins?.[index].position.x
                                    }
                                });
                            }}
                            setText={(text) => {
                                setPin(index, { text: text });
                            }}
                            setShowRemoveEffect={setShowRemoveEffect}
                            remove={() => {
                                removePin(index);
                            }}
                        />
                    ))}
                    {placingNewPin && <PinRight
                        calcYear={calcYear}
                        position={newPinPos}
                    />}
                </div>
            </div>
            <div className="absolute bottom-0 right-0 flex gap-4">
                <Dialog
                    trigger={<Button>Kom igång</Button>}
                    title="Kom igång"
                >
                    <TaskMarkdown>{guide}</TaskMarkdown>
                </Dialog>
                <Button
                    variant="success"
                    onClick={() => {
                        const newState: TimelineData = {
                            ...data,
                            completed: true,
                        };
                        setData(newState);
                        onStateChanged?.(newState);
                        onDone?.();
                    }}
                >
                    Klar
                </Button>
            </div>
        </div>
    );
}

export function TimelineTaskDialog({ initialState, onStateChanged }: TimelineTaskProps) {
    const handleTimelineResizeRef = useRef<() => void>(null);
    const [open, setOpen] = useState(false);

    return (
        <BaseDialog.Dialog open={open} onOpenChange={(open) => setOpen(open)}>
            <BaseDialog.DialogTrigger asChild>
                <Button
                    variant={initialState?.completed ? "success" : "step-current"}
                >
                    Din Tvättlina
                </Button>

            </BaseDialog.DialogTrigger>
            <BaseDialog.DialogContent
                onAnimationEnd={() => handleTimelineResizeRef.current()}
                aria-describedby={undefined}
                className="flex flex-col max-w-3xl h-[95dvh] w-[90%]"
                onOpenAutoFocus={(event) => {
                    // Don't auto focus first input in dialog
                    event.preventDefault();
                    // focus the dialog element for aria
                    (event.target as HTMLElement).focus();
                }}
            >
                <BaseDialog.DialogHeader>
                    <BaseDialog.DialogTitle>
                        Din Tvättlina
                    </BaseDialog.DialogTitle>
                    <img src="clothesline.png" className="absolute w-[80%]" />
                    <div className="h-12" />
                </BaseDialog.DialogHeader>
                <TimelineTask
                    handleResizeRef={handleTimelineResizeRef}
                    initialState={initialState}
                    onStateChanged={onStateChanged}
                    onDone={() => {
                        setOpen(false);
                    }}
                />
            </BaseDialog.DialogContent>
        </BaseDialog.Dialog >
    );
}

type PinRightProps = {
    calcYear?: (yPosition: number) => number;
    minY?: number;
    maxY?: number;
    position: Point;
    className?: string;
    pinClassName?: string;
    draggable?: boolean;
    text?: string;
    setPosition?: (position: Point) => void;
    setText?: (text: string) => void;
    setShowRemoveEffect?: (value: boolean) => void;
    remove?: () => void;
};
const PinRight = forwardRef<HTMLElement, PinRightProps>(
    function PinRight({ minY = 0, maxY = 1, remove, setShowRemoveEffect, calcYear, className, pinClassName, position, draggable = false, text, setPosition, setText }, ref) {
        const thisRef = useRef<HTMLDivElement>(null);
        const lineRef = useRef<HTMLDivElement>(null);
        const parentBoundsRef = useRef<DOMRect>(null);

        const [year, setYear] = useState<number>(calcYear(position.y));

        const dragPosition = useRef({ x: 0, y: 0 });
        const dragStart = useRef<Point>({ x: 0, y: 0 });

        useImperativeHandle(ref, () => thisRef.current);

        useEffect(() => {
            if (!thisRef.current) return;

            setYear(calcYear(position.y));
            setDisplayPosition(position);
        }, [calcYear, lineRef, position, ref]);

        const setDisplayPosition = (pos: Point) => {
            thisRef.current.style.top = `${pos.y * 100}%`;
            // lineRef.current.style.width = `${pos.x}px`;
            lineRef.current.style.width = `4rem`;
        };

        const updateY = (event: React.TouchEvent<HTMLDivElement>) => {
            const newY = position.y + (event.targetTouches[0].screenY - dragStart.current.y) / parentBoundsRef.current.height;
            const clampedNewY = clamp(newY, minY ?? 0, maxY ?? 1);
            setYear(calcYear(clampedNewY));
            dragPosition.current = {
                x: position.x,
                y: clampedNewY,
            };
            setShowRemoveEffect?.(clampedNewY < 0);
        };

        const handleDragStart = (event: React.TouchEvent<HTMLDivElement>) => {
            if (!draggable) return;

            parentBoundsRef.current = thisRef.current.parentElement.getBoundingClientRect();

            // console.log("drag start");
            dragStart.current = {
                x: event.targetTouches[0].screenX,
                y: event.targetTouches[0].screenY,
            };

            updateY(event);
            // console.log(dragStart.current, position)
        };
        const handleDragging = (event: React.TouchEvent<HTMLDivElement>) => {
            if (!draggable) return;
            // console.log("drag move");
            updateY(event);
            setDisplayPosition(dragPosition.current);
        };
        const handleDragEnd = () => {
            if (!draggable) return;
            // console.log("drag end");
            setPosition?.(dragPosition.current);
            setYear(calcYear(dragPosition.current.y));
            setDisplayPosition({ x: position.x, y: dragPosition.current.y });
            if (dragPosition.current.y < 0) {
                remove?.();
            }
            setShowRemoveEffect?.(false);
        };
        const handleTextInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
            setText(event.currentTarget.value);
        };

        return (
            <div
                ref={thisRef}
                className={cn(
                    "absolute -translate-y-1/2 flex flex-row items-center w-full",
                    className
                )}
                onFocus={(event) => {
                    event.currentTarget.style.zIndex = "100";
                }}
                onBlur={(event) => {
                    event.currentTarget.style.zIndex = "";
                }}
            >
                <div className="relative h-px pl-4" ref={lineRef} >
                    <div className="bg-black h-full" />
                    {!!year && !Number.isNaN(year) && (
                        <p className="absolute -left-6 top-1/2 -translate-y-1/2  text-muted-foreground text-sm pr-1">
                            {Math.round(year)}
                        </p>
                    )}
                </div>
                <Pin
                    onTouchStart={handleDragStart}
                    onTouchMove={handleDragging}
                    onTouchEnd={handleDragEnd}
                    className={cn("absolute", pinClassName)}
                />
                <Card className="grow">
                    <CardContent className="p-0.5 relative">
                        <Textarea
                            onlyOnFocus={true}
                            value={text}
                            onInput={handleTextInput}
                            rows={1}
                            className="outline-offset-[0.25rem] min-h-0">
                        </Textarea>
                    </CardContent>
                </Card>
            </div>
        );
    }
);

type PinProps = {
    className?: string;
    onTouchStart?: React.TouchEventHandler<HTMLDivElement>;
    onTouchMove?: React.TouchEventHandler<HTMLDivElement>;
    onTouchEnd?: React.TouchEventHandler<HTMLDivElement>;
};
function Pin({ className, onTouchStart, onTouchMove, onTouchEnd }: PinProps) {
    return <div
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchMove={onTouchMove}
        className={cn(
            "size-12 bg-[url('/img/clothespin-horizontal.png')] bg-contain bg-center bg-no-repeat",
            className
        )}
    />;
}